import React, { useState, useEffect } from 'react';
import VehicleUtilizationChart from '../components/VehicleUtilizationChart';
import RouteUtilizationChart from '../components/RouteUtilizationChart';
import { dashboardApi } from '../../../api/adminapi';

function Dashboard({ role }) {
  const [chartData, setChartData] = useState({
    bookingsPerRoute: [],
    seatUtilization: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      // Blind Integration API calls - these will be implemented in backend
      const [bookingsData, utilizationRaw] = await Promise.all([
        dashboardApi.getBookingsPerRoute().catch(() => getMockBookingsData()),
        dashboardApi.getSeatUtilization().catch(() => getMockUtilizationData())
      ]);

      setChartData({
        bookingsPerRoute: bookingsData,
        seatUtilization: normalizeSeatUtilization(utilizationRaw)
      });
    } catch (err) {
      setError('Failed to load dashboard data');
      // Fallback to mock data
      setChartData({
        bookingsPerRoute: getMockBookingsData(),
        seatUtilization: getMockUtilizationData()
      });
    } finally {
      setLoading(false);
    }
  };

  // Mock data fallbacks until backend APIs are ready
  const getMockBookingsData = () => [
    { route: 'Delhi to Noida', assigned: 10 },
    { route: 'Noida to Faridabad', assigned: 20 },
    { route: 'Faridabad to Gurgaon', assigned: 15 },
    { route: 'Gurgaon to Delhi', assigned: 25 },
    { route: 'Delhi to Gurgaon', assigned: 18 },
    { route: 'Noida to Delhi', assigned: 12 }
  ];

  const getMockUtilizationData = () => [
    { route: 'Delhi to Noida', utilization: 65, capacity: 100 },
    { route: 'Noida to Faridabad', utilization: 80, capacity: 100 },
    { route: 'Faridabad to Gurgaon', utilization: 45, capacity: 100 },
    { route: 'Gurgaon to Delhi', utilization: 90, capacity: 100 },
    { route: 'Delhi to Gurgaon', utilization: 72, capacity: 100 },
    { route: 'Noida to Delhi', utilization: 58, capacity: 100 }
  ];

  // Normalize backend seat-utilization payload to chart format
  const normalizeSeatUtilization = (payload) => {
    // Expected backend structure via ResponseHandler: { message, status, data: { location, routes: [...], count } }
    const routes = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data?.routes)
        ? payload.data.routes
        : Array.isArray(payload?.routes)
          ? payload.routes
          : [];

    return routes.map((r) => {
      const source = r?.source || '';
      const destination = r?.destination || '';
      const label = source && destination ? `${source} to ${destination}` : (r?.timing || 'Route');
      const utilizationPercent = typeof r?.utilizationPercent === 'number' ? r.utilizationPercent : Number(r?.utilizationPercent || 0);
      return {
        route: label,
        utilization: Math.round(utilizationPercent),
        capacity: typeof r?.totalSeats === 'number' ? r.totalSeats : Number(r?.totalSeats || 0)
      };
    });
  };

  if (loading) {
    return (
      <div className="admin-view">
        <div className="dashboard-header">
          <h1>Bus Utilization Dashboard</h1>
          <p className="dashboard-subtitle">Loading dashboard data...</p>
        </div>
        <div className="loading-spinner">Loading charts...</div>
      </div>
    );
  }

  return (
    <div className="admin-view">
      <div className="dashboard-header">
        <h1>Bus Utilization Dashboard</h1>
        <p className="dashboard-subtitle">Real-time Analytics - Fleet Management System</p>
        {error && (
          <div className="dashboard-error">
            ⚠️ {error} (Showing sample data)
          </div>
        )}
      </div>

      <div className="charts-container">
        <div className="chart-section">
          <h2>Seat Utilization Analysis</h2>
          <p className="chart-description">Utilization percentage and route performance metrics</p>
          <RouteUtilizationChart data={chartData.seatUtilization} />
        </div>

        <div className="chart-section">
          <h2>Assigned per Route</h2>
          <p className="chart-description">Number of assigned for each route</p>
          <VehicleUtilizationChart data={chartData.bookingsPerRoute} />
        </div>
      </div>
    </div>
  );
}

export default Dashboard;


